import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import { Input } from 'v5/design-system/Input';
import {
  SectionedChecklist,
  type SectionedChecklistItem,
  type SectionedChecklistSection
} from 'v5/patterns/widgets/SectionedChecklist';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

export interface CustomFieldCatalogItem {
  id: number;
  name: string;
  /** Short label for the field type (e.g. humanised enum). */
  typeLabel: string;
  /** ISO 8601 instant from the API; used to sort the list newest-first. */
  created_at: string;
}

/** A field group offered beside the fields. */
export interface CustomFieldGroupCatalogItem {
  id: number;
  name: string;
  memberCount: number;
  /** People can add as many blocks of it as they need. */
  isArray: boolean;
}

/** List keys: a field and a group may share an id. */
const fieldKey = (id: number) => `field:${id}`;
const groupKey = (id: number) => `group:${id}`;

function catalogCreatedAtMs(item: CustomFieldCatalogItem): number {
  const t = Date.parse(item.created_at);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

const EMPTY_LOCKED_ASSIGNMENT_IDS = new Set<number>();
const NO_GROUPS: CustomFieldGroupCatalogItem[] = [];

export interface CustomFieldAddSearchStageProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  catalog: CustomFieldCatalogItem[];
  value: ReadonlySet<number>;
  onChange?: (nextAssignedIds: ReadonlySet<number>) => void;
  /** Already-assigned ids that cannot be unassigned here. */
  lockedAssignmentIds?: ReadonlySet<number>;
  onOpenCreateFromSearchQuery: (prefillName: string) => void;
  /** Field groups, listed in a section of their own after the fields. */
  groupCatalog?: CustomFieldGroupCatalogItem[];
  groupValue?: ReadonlySet<number>;
  onGroupChange?: (nextAssignedIds: ReadonlySet<number>) => void;
  lockedGroupIds?: ReadonlySet<number>;
}

export function CustomFieldAddSearchStage({
  searchQuery,
  onSearchQueryChange,
  catalog,
  value,
  onChange,
  lockedAssignmentIds = EMPTY_LOCKED_ASSIGNMENT_IDS,
  onOpenCreateFromSearchQuery,
  groupCatalog = NO_GROUPS,
  groupValue = EMPTY_LOCKED_ASSIGNMENT_IDS,
  onGroupChange,
  lockedGroupIds = EMPTY_LOCKED_ASSIGNMENT_IDS
}: CustomFieldAddSearchStageProps) {
  const { t } = useTranslation('patterns');
  const normalizedQuery = searchQuery.trim();
  const normalizedQueryLower = normalizedQuery.toLowerCase();

  const showCreateFromSearch =
    normalizedQuery.length > 0 &&
    !catalog.some(f => f.name.toLowerCase() === normalizedQueryLower);

  const filtered = useMemo(() => {
    const matches = normalizedQueryLower
      ? catalog.filter(f => f.name.toLowerCase().includes(normalizedQueryLower))
      : [...catalog];
    matches.sort((a, b) => catalogCreatedAtMs(b) - catalogCreatedAtMs(a));
    return matches;
  }, [catalog, normalizedQueryLower]);

  const filteredGroups = useMemo(
    () =>
      normalizedQueryLower
        ? groupCatalog.filter(group =>
            group.name.toLowerCase().includes(normalizedQueryLower)
          )
        : groupCatalog,
    [groupCatalog, normalizedQueryLower]
  );

  const hasListContent = filtered.length > 0 || filteredGroups.length > 0;
  const showEmpty =
    !hasListContent &&
    !showCreateFromSearch &&
    catalog.length === 0 &&
    groupCatalog.length === 0;
  const withSections = groupCatalog.length > 0;

  const alreadyAdded = (
    <Badge variant="gray" tone="subtle" size="sm">
      {t('CustomFieldAddFlow.alreadyAdded')}
    </Badge>
  );
  const fieldItems = filtered.map(
    (field): SectionedChecklistItem => ({
      id: fieldKey(field.id),
      label: field.name,
      badges: <TypeBadge>{field.typeLabel}</TypeBadge>,
      extra: lockedAssignmentIds.has(field.id) ? alreadyAdded : undefined
    })
  );
  const groupItems = filteredGroups.map(
    (group): SectionedChecklistItem => ({
      id: groupKey(group.id),
      label: group.name,
      badges: (
        <>
          <TypeBadge>
            {t('CustomFieldAddFlow.groupFieldCount', {
              count: group.memberCount
            })}
          </TypeBadge>
          {group.isArray && (
            <TypeBadge>{t('CustomFieldAddFlow.groupMultiple')}</TypeBadge>
          )}
        </>
      ),
      extra: lockedGroupIds.has(group.id) ? alreadyAdded : undefined
    })
  );
  const sections: SectionedChecklistSection[] = withSections
    ? [
        {
          id: 'fields',
          heading: t('CustomFieldAddFlow.fieldsSection'),
          items: fieldItems
        },
        {
          id: 'groups',
          heading: t('CustomFieldAddFlow.groupsSection'),
          items: groupItems
        }
      ].filter(section => section.items.length > 0)
    : [{ id: 'fields', items: fieldItems }];

  const selected = new Set([
    ...[...value].map(fieldKey),
    ...[...groupValue].map(groupKey)
  ]);
  const disabledIds = [
    ...(onChange ? [] : filtered.map(field => fieldKey(field.id))),
    ...(onGroupChange ? [] : filteredGroups.map(group => groupKey(group.id)))
  ];
  const lockedIds = [
    ...[...lockedAssignmentIds].map(fieldKey),
    ...[...lockedGroupIds].map(groupKey)
  ];
  // one change can tick fields and groups together (a Shift range): each kind gets its own set
  const handleChange = (keys: ReadonlySet<string>) => {
    const idsOf = (kind: 'field' | 'group') =>
      new Set(
        [...keys]
          .filter(key => key.startsWith(`${kind}:`))
          .map(key => Number(key.slice(kind.length + 1)))
      );
    const same = (a: ReadonlySet<number>, b: ReadonlySet<number>) =>
      a.size === b.size && [...a].every(id => b.has(id));
    const fields = idsOf('field');
    const groups = idsOf('group');
    if (!same(fields, value)) onChange?.(fields);
    if (!same(groups, groupValue ?? new Set())) onGroupChange?.(groups);
  };

  const renderListContent = () => {
    if (showEmpty) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t('CustomFieldAddFlow.noCatalog')}
          </p>
        </div>
      );
    }

    if (!hasListContent && !showCreateFromSearch) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t(
              'CustomFieldAddFlow.noFieldsAvailable',
              normalizedQuery ? { context: 'search' } : undefined
            )}
          </p>
        </div>
      );
    }

    if (!hasListContent) {
      return null;
    }

    return (
      <SectionedChecklist
        aria-label={t('CustomFieldAddFlow.selectFieldsToAdd')}
        // rows reach the edges, as in the field chooser's catalog
        className="-mx-4"
        sections={sections}
        selected={selected}
        onChange={handleChange}
        disabledIds={disabledIds}
        lockedIds={lockedIds}
        emptyState={t('CustomFieldAddFlow.noFieldsAvailable', {
          context: 'search'
        })}
      />
    );
  };

  // laid out as the field chooser's catalog column: the search stuck on top, a rule under it once
  // the list scrolls beneath
  return (
    <ScrollShadowWrapper
      edge="soft"
      renderTopShadow={false}
      className="min-h-0 flex-1"
      scrollerClassName="relative with-styled-scrollbars"
    >
      <div className="sticky top-0 z-10 border-b border-transparent bg-bg-default px-4 pt-4 pb-2 [[data-has-top-shadow]>&]:border-border-default">
        <Input
          size="sm"
          aria-label={t('CustomFieldAddFlow.searchPlaceholder')}
          placeholder={t('CustomFieldAddFlow.searchPlaceholder')}
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2 px-4 pt-1 pb-4">
        {showCreateFromSearch && (
          // its text in line with the rows' labels
          <div className="-mx-4 px-1.5">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenCreateFromSearchQuery(normalizedQuery)}
              className="flex w-full items-center gap-3 px-1.5 py-2 text-left"
            >
              <Plus className="size-4 shrink-0 text-fg-muted" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {t('CustomFieldAddFlow.createNamed', {
                    name: normalizedQuery
                  })}
                </p>
                <p className="text-xs text-fg-muted">
                  {t('CustomFieldAddFlow.addAsNewHint')}
                </p>
              </div>
            </Button>
          </div>
        )}
        {renderListContent()}
      </div>
    </ScrollShadowWrapper>
  );
}

function TypeBadge({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant="gray"
      tone="subtle"
      size="sm"
      className="shrink-0 border border-border-default px-1.5 py-0 text-xs font-normal uppercase tracking-wide"
    >
      {children}
    </Badge>
  );
}
